import { intranetLibraryPath, PermissionCode, ProductCode } from '@gb-ticket/shared-types';
import { Navigate, useLocation, useParams, type RouteObject } from 'react-router';

import {
  RequireAuthLayout,
  RequirePermissionLayout,
  RequireProductLayout,
} from '@/platform/auth/require-permission';
import { PlatformShellLayout } from '@/platform/layouts/platform-shell';
import '@/products/intranet/profile-sections';
import '@/products/intranet/product-nav';
import {
  INTRANET_CHANNEL_COMPOSE_PATH,
  INTRANET_EDITORIAL_SHORTCUTS_PATH,
  INTRANET_LIBRARY_PATH,
  INTRANET_ROOT,
  intranetEditPath,
} from '@/products/intranet/intranet-paths';
import { IntranetLayout } from '@/products/intranet/layouts/intranet-layout';
import { ColleaguesPage } from '@/products/intranet/pages/colleagues-page';
import { ChatPage } from '@/products/intranet/pages/chat-page';
import { ContactsPage } from '@/products/intranet/pages/contacts-page';
import { IntranetHomePage } from '@/products/intranet/pages/intranet-home-page';
import { LegacySearchRedirect } from '@/products/intranet/pages/legacy-search-redirect';
import { NewsDetailPage } from '@/products/intranet/pages/news-detail-page';
import { LibraryDetailPage } from '@/products/intranet/pages/library-detail-page';
import { LibraryListPage } from '@/products/intranet/pages/library-list-page';
import { KnowledgeCategoriesManagePage } from '@/products/intranet/pages/knowledge-categories-manage-page';
import { KnowledgeQuickAccessPage } from '@/products/intranet/pages/knowledge-quick-access-page';
import { KnowledgeCategoryPage } from '@/products/intranet/pages/knowledge-category-page';
import { AcknowledgementsReportPage } from '@/products/intranet/pages/editorial/acknowledgements-report-page';
import { CategoriesPage } from '@/products/intranet/pages/editorial/categories-page';
import { ContactsManagePage } from '@/products/intranet/pages/editorial/contacts-manage-page';
import { ContentFormPage } from '@/products/intranet/pages/editorial/content-form-page';
import { EditorialDashboardPage } from '@/products/intranet/pages/editorial/editorial-dashboard-page';
import { AppearancePage } from '@/products/intranet/pages/editorial/appearance-page';
import { SettingsPage } from '@/products/intranet/pages/editorial/settings-page';
import { ShortcutsManagePage } from '@/products/intranet/pages/editorial/shortcuts-manage-page';
import { IntranetAiPage } from '@/products/intranet/pages/intranet-ai-page';
import { SiteDiaryPage } from '@/products/intranet/pages/site-diary-page';
import { SiteDiaryReportPage } from '@/products/intranet/pages/site-diary-report-page';

export function LegacyEditorialComposeRedirect() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: INTRANET_CHANNEL_COMPOSE_PATH, search }} replace />;
}

export function LegacyEditorialEditRedirect() {
  const { slug = '' } = useParams();
  return <Navigate to={intranetEditPath(slug)} replace />;
}

export function LegacyLibraryEntryRedirect() {
  const { slug = '' } = useParams();
  return <Navigate to={intranetLibraryPath(slug)} replace />;
}

/** Intranet product (`/intranet/*`) inside the Mitarbeiterportal chrome. */
// eslint-disable-next-line react-refresh/only-export-components -- route config alongside redirect components
export const intranetRoute: RouteObject = {
  Component: RequireAuthLayout,
  children: [
    {
      Component: PlatformShellLayout,
      children: [
        {
          path: INTRANET_ROOT,
          element: <RequireProductLayout product={ProductCode.INTRANET} />,
          children: [
            {
              Component: IntranetLayout,
              children: [
                { index: true, Component: IntranetHomePage },
                { path: 'suche', element: <LegacySearchRedirect /> },
                { path: 'nachrichten', element: <Navigate to={INTRANET_ROOT} replace /> },
                { path: 'nachrichten/:slug', Component: NewsDetailPage },
                { path: 'wissensdatenbank', Component: LibraryListPage },
                {
                  path: 'wissensdatenbank/kategorien',
                  element: (
                    <RequirePermissionLayout
                      permission={PermissionCode.INTRANET_CATEGORIES_MANAGE}
                      deniedMessage="Die Kategorien der Wissensdatenbank sind nur für die Redaktion."
                    />
                  ),
                  children: [{ index: true, Component: KnowledgeCategoriesManagePage }],
                },
                {
                  path: 'wissensdatenbank/schnellzugriff',
                  element: (
                    <RequirePermissionLayout
                      permission={PermissionCode.INTRANET_CONTENT_MANAGE}
                      deniedMessage="Der Schnellzugriff ist nur mit dem Verwalten-Recht zu pflegen."
                    />
                  ),
                  children: [{ index: true, Component: KnowledgeQuickAccessPage }],
                },
                { path: 'wissensdatenbank/kategorie/:kurzname', Component: KnowledgeCategoryPage },
                { path: 'wissensdatenbank/:slug', Component: LibraryDetailPage },
                { path: 'bibliothek', element: <Navigate to={INTRANET_LIBRARY_PATH} replace /> },
                { path: 'bibliothek/:slug', element: <LegacyLibraryEntryRedirect /> },
                { path: 'kollegen', Component: ColleaguesPage },
                { path: 'chat', Component: ChatPage },
                { path: 'chat/:conversationId', Component: ChatPage },
                {
                  path: 'ki',
                  element: (
                    <RequirePermissionLayout
                      permission={PermissionCode.INTRANET_AI_USE}
                      deniedMessage="intranet.ai.chat-denied"
                    />
                  ),
                  children: [{ index: true, Component: IntranetAiPage }],
                },
                { path: 'ansprechpartner', Component: ContactsPage },
                { path: 'modul/:instanceId', Component: SiteDiaryPage },
                {
                  path: 'modul/:instanceId/bericht/:reportId',
                  Component: SiteDiaryReportPage,
                },
                { path: 'beitrag/neu', Component: ContentFormPage },
                { path: 'beitrag/:slug/bearbeiten', Component: ContentFormPage },
                { path: 'redaktion/neu', element: <LegacyEditorialComposeRedirect /> },
                {
                  path: 'redaktion/:slug/bearbeiten',
                  element: <LegacyEditorialEditRedirect />,
                },
                {
                  path: 'redaktion',
                  element: (
                    <RequirePermissionLayout
                      permission={PermissionCode.INTRANET_CONTENT_WRITE}
                      deniedMessage="Die Redaktion ist nur für Intranet-Redakteure."
                    />
                  ),
                  children: [
                    { index: true, Component: EditorialDashboardPage },
                    {
                      path: ':slug/bestaetigungen',
                      element: (
                        <RequirePermissionLayout
                          permission={PermissionCode.INTRANET_ACKNOWLEDGEMENTS_READ}
                          deniedMessage="intranet.ack.report-denied"
                        />
                      ),
                      children: [{ index: true, Component: AcknowledgementsReportPage }],
                    },
                    {
                      path: 'kategorien',
                      element: (
                        <RequirePermissionLayout
                          permission={PermissionCode.INTRANET_CATEGORIES_MANAGE}
                          deniedMessage="intranet.category.manage-denied"
                        />
                      ),
                      children: [{ index: true, Component: CategoriesPage }],
                    },
                    {
                      path: 'ansprechpartner',
                      element: (
                        <RequirePermissionLayout
                          permission={PermissionCode.INTRANET_CONTACTS_MANAGE}
                          deniedMessage="intranet.contact.manage-denied"
                        />
                      ),
                      children: [{ index: true, Component: ContactsManagePage }],
                    },
                    { path: 'erscheinungsbild', Component: AppearancePage },
                    { path: 'einstellungen', Component: SettingsPage },
                    {
                      path: 'schnellzugriff',
                      element: (
                        <RequirePermissionLayout
                          permission={PermissionCode.INTRANET_LINKS_MANAGE}
                          deniedMessage="intranet.quicklink.manage-denied"
                        />
                      ),
                      children: [{ index: true, Component: ShortcutsManagePage }],
                    },
                    {
                      path: 'links',
                      element: <Navigate to={INTRANET_EDITORIAL_SHORTCUTS_PATH} replace />,
                    },
                  ],
                },
                { path: '*', element: <Navigate to="." replace /> },
              ],
            },
          ],
        },
      ],
    },
  ],
};
